/*
 * Defines interfaces and functions specific to TPRM. Members shared with UCRM are defined in `Models/Organization.ts`, `Models/AssessmentSubject.ts`, `Models/Folders.ts`, `Models/InherentRisk.ts`, `Models/DueDiligence.ts`, `Models/DueDiligenceWorkflow.ts`, and `Models/RiskRating.ts`.
 */
import { AssessmentSubjectBase, AssessmentSubjectForNonPrivilegedUserBase, ModuleEntities, OrganizationWithAssessmentSubjectsForNonPrivilegedUserBase, UpdateAssessmentSubjectRequestBase } from './AssessmentSubject';
import { DiscussionMessageType, DueDiligenceAssessmentBase, DueDiligenceQuestion } from './DueDiligence';
import { CompleteIntakeRequestBase, DueDiligenceWorkflowArchiveBase } from './DueDiligenceWorkflow';
import { SaveOrSubmitInherentRiskQuestionnaireRequestBase, SubmitInherentRiskQuestionnaireSelectionsBase } from './InherentRisk';
import { CreateOrganizationRequestBase, FinalizationState, OrganizationBase, OrganizationForNonPrivilegedUserBase, OrganizationResponseBase, UpdateOrganizationRequestBase } from './Organization';
import { DisplayableTag } from './Tags';

/** A subset of Third-Party Service that is duplicated for other features that map to Third-Party Services. Kept up to date by the back-end when the Third-Party Service changes. */
export interface AssociatedThirdPartyService {
    id: string;
    name: string;
    vendor_id: string; // The ID of the TPRM third party.
    vendor_name: string; // The name of the TPRM third party.
}

/**
 * Contact information for Third-Party personnel. Used for both Third Party and Third-Party Service contact information.
 */
export interface ThirdPartyContact {
    name?: string;
    email_address?: string;
    phone_number?: string;
    additional_information?: string;
}

export interface CreateThirdPartyRequest extends CreateOrganizationRequestBase {
    country: string;
    vendor_contacts: ThirdPartyContact[];

    address_line_1?: string;
    address_line_2?: string;
    city?: string;
    ein?: string;
    state?: string;
    website?: string;
    zip_code?: string;
}

export interface UpdateThirdPartyRequest extends UpdateOrganizationRequestBase {
    address_line_1?: string;
    address_line_2?: string;
    city?: string;
    country?: string;
    ein?: string;
    state?: string;
    vendor_contacts?: ThirdPartyContact[];
    website?: string;
    zip_code?: string;
}

export interface ThirdParty extends OrganizationBase {
    country: string;
    vendor_contacts: ThirdPartyContact[]; // Optional from a user perspective, but not from a code perspective since this will always at a minimum be an empty list.

    address_line_1?: string;
    address_line_2?: string;
    city?: string;
    created_by?: string; // Optional because of legacy data. All new third parties have a value for this field.
    ein?: string;
    state?: string;
    website?: string;
    zip_code?: string;
}

export interface ThirdPartyForNonTprmUser extends OrganizationForNonPrivilegedUserBase {
    country: string;
    vendor_contacts: ThirdPartyContact[];

    address_line_1?: string;
    address_line_2?: string;
    city?: string;
    created_by?: string; // Optional because of legacy data. All new third parties have a value for this field.
    ein?: string;
    state?: string;
    website?: string;
    zip_code?: string;
}

export interface ThirdPartyResponse extends OrganizationResponseBase {
    country: string;
    vendor_contacts: ThirdPartyContact[];

    address_line_1?: string;
    address_line_2?: string;
    city?: string;
    created_by?: string; // Optional because of legacy data. All new third parties have a value for this field.
    ein?: string;
    state?: string;
    website?: string;
    zip_code?: string;
    [key: string]: any; // TODO: Remove.
}

// TODO: Can we consolidate this with ThirdPartyResponse?
export interface ThirdPartyResponseWithServices extends ThirdPartyResponse {
    services: ThirdPartyService[];
    [key: string]: any; // TODO: Remove.
}

export interface ThirdPartyWithServicesForNonTprmUser extends OrganizationWithAssessmentSubjectsForNonPrivilegedUserBase {
    country: string;
    services: ServiceForNonTprmUser[];
    vendor_contacts: ThirdPartyContact[];

    address_line_1?: string;
    address_line_2?: string;
    city?: string;
    created_by?: string; // Optional because of legacy data. All new third parties have a value for this field.
    ein?: string;
    state?: string;
    website?: string;
    zip_code?: string;
}

export interface ServiceForNonTprmUser extends AssessmentSubjectForNonPrivilegedUserBase {
    vendor_contacts: ThirdPartyContact[]; // This property is called "vendor_contacts" because of the `ThirdPartyContact` interface, but note that these are the contacts for the _service_. This is not the same data as `ThirdParty.vendor_contacts`.

    created_by?: string; // Optional because of legacy data. All new services have a value for this field.
    responsible_organization?: string;
}

export interface ThirdPartyService extends AssessmentSubjectBase {
    vendor_contacts: ThirdPartyContact[]; // This property is called "vendor_contacts" because of the `ThirdPartyContact` interface, but note that these are the contacts for the _service_. This is not the same data as `ThirdParty.vendor_contacts`.

    created_by?: string; // Optional because of legacy data. All new services have a value for this field.
    responsible_organization?: string;
}

/** Used to update a third-party service. See the warning on `UpdateAssessmentSubjectRequestBase`. */
export interface UpdateServiceRequest extends UpdateAssessmentSubjectRequestBase {
    responsible_organization?: string;
    vendor_contacts?: ThirdPartyContact[];
}

export interface CreateServiceRequest extends UpdateServiceRequest {
    finalization_state: FinalizationState;
}

export type ThirdPartyArchive = Omit<ThirdParty, 'tags'> & { tags: DisplayableTag[] };

export type ThirdPartyServiceArchive = Omit<ThirdPartyService, 'tags'> & { tags: DisplayableTag[] };

export interface TprmDueDiligenceWorkflowArchive extends DueDiligenceWorkflowArchiveBase {
    assessments: TprmDueDiligenceAssessment[];
    service: ThirdPartyServiceArchive;
    third_party: ThirdPartyArchive;
}

export interface TprmSubmitInherentRiskQuestionnaireSelections extends SubmitInherentRiskQuestionnaireSelectionsBase {
    third_party_contacts: ThirdPartyContact[]; // The list of third-party service contacts to invite to complete questionnaires.
}

export interface TprmSaveOrSubmitInherentRiskQuestionnaireRequest extends SaveOrSubmitInherentRiskQuestionnaireRequestBase {
    submit_selections: TprmSubmitInherentRiskQuestionnaireSelections; // Ignored if `submit` is false.
}

export const DEFAULT_TPRM_INTAKE_INSTRUCTIONS = 'Create a new third party and/or third-party service, and optionally complete the inherent risk questionnaire. Items are saved as drafts until the Submit step is completed.';

export interface CompleteTprmIntakeRequest extends CompleteIntakeRequestBase {
    submit_irq_selections: TprmSubmitInherentRiskQuestionnaireSelections; // Ignored if `submit_irq` is false.
}

/** A TPRM due diligence assessment, whose questionnaire is completed by third-party contacts. */
export interface TprmDueDiligenceAssessment extends DueDiligenceAssessmentBase {
    questionnaire_submitted_by?: string; // The email address of the third-party contact who submitted the questionnaire.
}

export interface InviteThirdPartyContactsRequest {
    contacts: ThirdPartyContact[];
}

export interface PublicClientAndThirdPartyServiceDetails {
    client_name: string;
    service_name: string;
    third_party_name: string;
}

export interface PublicQuestionnaireRegistrationRequest {
    client_id: string;
    third_party_id: string;
    service_id: string;
    email_address: string;
    password: string;
}

export interface PublicControl {
    identifier: string;
    name: string;
    text: string;
    questions: DueDiligenceQuestion[];
}

export interface PublicControlGroup {
    controls: PublicControl[];
    description: string;
    identifier: string;
    name: string;
}

export interface PublicControlFramework {
    control_groups: PublicControlGroup[];
    identifier: string;
    name: string;
}

export interface PublicDiscussionMessageClient {
    _type: DiscussionMessageType.CLIENT;
    text: string;
    timestamp: string;
    user_first_name: string;
    user_last_name: string;
}

export interface PublicDiscussionMessageThirdParty {
    _type: DiscussionMessageType.THIRD_PARTY;
    text: string;
    timestamp: string;
    user_email_address: string;
}

export type PublicDiscussionMessage = PublicDiscussionMessageClient | PublicDiscussionMessageThirdParty;

export interface PublicDiscussion {
    marked_as_read_timestamps_third_party: { [emailAddress: string]: string };
    messages: PublicDiscussionMessage[];
}

export interface PublicQuestionnaire {
    control_frameworks: PublicControlFramework[];
    discussion: PublicDiscussion;
    id: string;
    name: string;
    service_id: string;
    third_party_id: string;

    due_date?: string;
    submitted_timestamp?: string;
}

export interface PublicAddDiscussionMessageRequest {
    text: string;
}

/** The TPRM entity types, for the UI that is shared by TPRM and UCRM. See `ModuleEntities`. */
export interface TprmEntities extends ModuleEntities {
    organization: ThirdPartyResponse;
    organizationForNonPrivilegedUser: ThirdPartyForNonTprmUser;
    organizationWithSubjects: ThirdPartyResponseWithServices;
    organizationWithSubjectsForNonPrivilegedUser: ThirdPartyWithServicesForNonTprmUser;
    subject: ThirdPartyService;
    subjectForNonPrivilegedUser: ServiceForNonTprmUser;
}
